import type { InputHTMLAttributes, ReactNode, Ref } from 'react';
import { useId } from 'react';
import { cx } from '@/lib/cx';

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  ref?: Ref<HTMLInputElement>;
  label: ReactNode;
  /** Secondary line under the label, for explaining a consequence. */
  description?: ReactNode;
}

/**
 * A real `<input type="radio">` with a label, rather than a styled div.
 *
 * ⚠️ **The house rule is "fix the primitive rather than working around it at
 * the call site", and this exists because there was no radio primitive at all.**
 * Before it, the only `type="radio"` in the entire app was hand-rolled inside
 * `SendForSignatureModal`, so the second caller that needed one was going to
 * hand-roll a second copy and the two would drift on focus ring, hit area and
 * disabled colour — none of which is visible in a diff.
 *
 * ⚠️ **This is deliberately a single RADIO, not a `RadioGroup` taking an
 * `options` array.** The case that motivated it — marking one row of a
 * repeating editor as primary — interleaves each control with the row's own
 * name, email and group fields, so a grouped component rendering its own list
 * could not express it. Exclusivity comes from the shared `name` prop, which is
 * the platform's own mechanism and gives arrow-key navigation and a single tab
 * stop for free. **A caller MUST pass a `name` shared by the set**, or every
 * radio becomes independently checkable and the "exactly one" promise is a lie
 * that still looks right on screen.
 *
 * Structurally identical to `Checkbox` on purpose — same gap, same hit area,
 * same description slot — so the two read as one family. The only visual
 * difference is `rounded-full`, which is the whole convention telling a reader
 * that this choice is exclusive and that one is `rounded-xs`.
 *
 * The whole row is the label, so the hit area is the text as well as the
 * control — a 16px target alone fails the 44px rule on a phone.
 */
export function Radio({ label, description, className, id, ...rest }: RadioProps) {
  const generated = useId();
  const inputId = id ?? generated;
  const descriptionId = description ? `${inputId}-description` : undefined;

  return (
    <div className={cx('flex gap-2.5', className)}>
      <input
        id={inputId}
        type="radio"
        aria-describedby={descriptionId}
        className="peer mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded-full border-border accent-accent-brand disabled:cursor-not-allowed disabled:opacity-50"
        {...rest}
      />
      <div className="min-w-0 peer-disabled:opacity-50 peer-disabled:[&_label]:cursor-not-allowed">
        <label htmlFor={inputId} className="cursor-pointer text-sm text-fg-secondary select-none">
          {label}
        </label>
        {description && (
          <p id={descriptionId} className="mt-0.5 text-xs text-fg-muted">
            {description}
          </p>
        )}
      </div>
    </div>
  );
}
